import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MotifAvatarModule,
  MotifChipModule,
  MotifDropdownModule,
  MotifFormsModule,
  MotifIconModule,
  Option,
} from '@ey-xd/ng-motif';

import { GraphApiService } from '@services/graph-api.service';
import {
  MotifActionIcPermContactCalendar24px,
  IconoirNavArrowDown,
  MotifContentIcSave24px,
} from '@ey-xd/motif-icon';
import {
  catchError,
  concat,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { ChipDropdownComponent } from '@shared/chip-dropdown/chip-dropdown.component';
import { Guid } from '@syncfusion/ej2-pdf-export';

@Component({
  selector: 'app-user-search',
  imports: [
    CommonModule,
    MotifFormsModule,
    TranslatePipe,
    TranslateDirective,
    ReactiveFormsModule,
    MotifChipModule,
    FormsModule,
    MotifAvatarModule,
    MotifIconModule,
    MotifDropdownModule,
    ChipDropdownComponent,
  ],
  templateUrl: './user-search.component.html',
  styleUrl: './user-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSearchComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class UserSearchComponent {
  contactIcon = MotifActionIcPermContactCalendar24px;
  arrowDownIcon = IconoirNavArrowDown;
  saveIcon = MotifContentIcSave24px;
  userInput$ = new Subject<string>();
  userList$: Observable<any[]>;
  userListLoading: boolean;
  minLengthTerm = 3;
  selectedRole: string;
  isDisabled: boolean = true;
  selectedUser: any;
  disabled: boolean = false;
  @Input() inlineLabel: string;
  @Input() shareUser: boolean = false;
  @Input() labelPosition = 'inside';
  @Input() preselectedUser: { displayName?: string; email?: string } | null =
    null;
  @Output() selectedUserInfo = new EventEmitter<any>();
  @ViewChild('searchInput') searchInput: ElementRef;
  @Input() statusList: any = [];
  @Input() ShowValidationStar: boolean = false;
  @Input() isNonEYUser: boolean = false;
  externalUser: string = '';
  readonly searchInputId = 'userSearchInput-' + Guid.getNewGuidString();

  // Returns true when externalUser has non-whitespace characters
  get isValidInput(): boolean {
    return !!(this.externalUser && this.externalUser.trim());
  }

  constructor(private readonly graphAPIService: GraphApiService) {}

  ngOnInit(): void {
    this.loadUserSuggestionList();
  }

  ngOnChanges(changes: any): void {
    if (changes['preselectedUser']) {
      const user = changes['preselectedUser'].currentValue;
      if (user) {
        this.selectedUser = { displayName: user.displayName, mail: user.email };
        setTimeout(() => {
          if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.value =
              user.displayName || user.email || '';
          }
        });
      } else {
        this.selectedUser = null;
        setTimeout(() => {
          if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.value = '';
          }
        });
      }
    }
    if (changes['statusList']) {
      this.statusList = changes['statusList'].currentValue;
      this.selectedRole = this.statusList.length > 0 ? this.statusList[0] : '';
    }
  }

  // This will be called when the form control value changes
  onChange = (value: any) => {};

  // This will be called when the form control is touched
  onTouched = () => {};

  writeValue(value: any): void {
    this.selectedUser = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getUserByEmail(inputValue): Observable<any[]> {
    return this.graphAPIService.searchUsers(inputValue).pipe(
      map((resp) => {
        let response: any = resp;
        let items: any[] = response.map((v) => {
          let displayName = v.displayName || '';
          let mail = v.mail || '';
          let givenName = v.givenName || '';
          let surname = v.surname || '';
          let uname = `${givenName} ${surname}`.trim();
          return {
            label: `${displayName} | ${mail} | ${givenName} | ${surname} | ${uname}`,
            Name: displayName,
            Email: mail,
            data: v,
          };
        }) as any[];
        return items;
      }),
    );
  }

  onInputChange(evt: Event): void {
    const inputValue = (evt.target as HTMLInputElement).value;
    this.userInput$.next(inputValue);
  }

  loadUserSuggestionList(): void {
    this.userList$ = concat(
      of([]), // default items
      this.userInput$.pipe(
        filter((res) => res !== null && res.length >= this.minLengthTerm),
        distinctUntilChanged(),
        debounceTime(800),
        tap(() => (this.userListLoading = true)),
        switchMap((term) =>
          this.getUserByEmail(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.userListLoading = false)),
          ),
        ),
      ),
    );
  }

  selectEvent(event) {
    this.isDisabled = false;
    this.selectedUser = event.data;
    event.label = event.Name;
    if (this.shareUser)
      this.selectedUserInfo.emit({
        user: this.selectedUser,
        role: this.selectedRole,
      });
    else this.selectedUserInfo.emit(event.data);
    this.onTouched();
  }

  selectUserRoleEvent($event) {
    if (this.selectedUser == null) {
      return;
    }
    if ($event) {
      this.selectedRole = $event;
      this.selectedUserInfo.emit({
        user: this.selectedUser,
        role: this.selectedRole,
      });
      this.onTouched();
    }
  }

  clearOption(event: Event) {
    this.selectedUser = null;
    this.isDisabled = true;
    this.selectedRole = this.statusList[0];
    this.selectedUserInfo.emit(null);
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
  }

  addExternalUser() {
    let viewerRole = this.statusList.find(
      (role) => role.RoleName.toLowerCase() === 'viewer',
    );
    if (viewerRole) {
      this.selectedRole = viewerRole;
    }
    this.selectedUser = {
      displayName: this.externalUser,
      mail: `${this.externalUser}@externaluser.com`,
      userId: Guid.getNewGuidString(),
      id: Guid.getNewGuidString(),
      givenName: this.externalUser,
      surname: this.externalUser,
      jobTitle: 'External User',
      isExternalUser: true,
    };
    this.selectedUserInfo.emit({
      user: this.selectedUser,
      role: this.selectedRole,
    });
    this.externalUser = '';
  }
}
